import { API_CONFIG, buildApiUrl } from "../config/api";
import { ApiResponse } from "../types";
import {
  TokenConfig,
  TokenRecord,
  CreateTokenConfigDto,
  UpdateTokenConfigDto,
  TokenOperationDto,
  UserTokenInfo,
  MyTokenBalance,
  TokenRecordListResponse,
} from "../types/token";
import { http, buildUrl } from "./base";

/**
 * Token管理相关 API 服务
 */
export class TokenService {
  /**
   * 获取所有Token配置
   */
  static async getAllConfigs(): Promise<ApiResponse<TokenConfig[]>> {
    const url = buildUrl(API_CONFIG.ENDPOINTS.TOKEN.CONFIGS);
    return http.get(url);
  }

  /**
   * 创建Token配置
   * @param data 配置数据
   */
  static async createConfig(
    data: CreateTokenConfigDto
  ): Promise<ApiResponse<TokenConfig>> {
    const url = buildUrl(API_CONFIG.ENDPOINTS.TOKEN.CONFIGS);
    return http.post(url, data);
  }

  /**
   * 更新Token配置
   * @param id 配置ID
   * @param data 配置数据
   */
  static async updateConfig(
    id: number,
    data: UpdateTokenConfigDto
  ): Promise<ApiResponse<TokenConfig>> {
    const url = buildApiUrl(API_CONFIG.ENDPOINTS.TOKEN.CONFIG_DETAIL, {
      id: String(id),
    });
    return http.patch(url, data);
  }

  /**
   * 获取我的Token余额
   */
  static async getMyBalance(): Promise<ApiResponse<MyTokenBalance>> {
    const url = buildUrl(API_CONFIG.ENDPOINTS.TOKEN.MY_BALANCE);
    return http.get(url);
  }

  /**
   * 获取我的Token使用记录
   */
  static async getMyLogs(): Promise<ApiResponse<TokenRecord[]>> {
    const url = buildUrl(API_CONFIG.ENDPOINTS.TOKEN.MY_LOGS);
    return http.get(url);
  }

  /**
   * Token消耗
   * @param data 消耗数据
   */
  static async consumeToken(
    data: TokenOperationDto
  ): Promise<ApiResponse<TokenRecord>> {
    const url = buildUrl(API_CONFIG.ENDPOINTS.TOKEN.CONSUME);
    return http.post(url, data);
  }

  /**
   * 获取指定用户的Token信息
   * @param accountId 账号ID
   */
  static async getUserToken(
    accountId: number
  ): Promise<ApiResponse<UserTokenInfo>> {
    const url = buildApiUrl(API_CONFIG.ENDPOINTS.TOKEN.USER_TOKEN, {
      accountId: String(accountId),
    });
    return http.get(url);
  }

  /**
   * 获取指定用户的Token使用记录
   * @param accountId 账号ID
   * @param phoneNumber 手机号
   * @param operationType 操作类型
   * @param startDate 开始日期
   * @param endDate 结束日期
   * @param page 页码
   * @param pageSize 每页数量
   */
  static async getUserTokenLogs(
    accountId?: number,
    phoneNumber?: string,
    operationType?: string,
    startDate?: string,
    endDate?: string,
    page?: number,
    pageSize?: number
  ): Promise<ApiResponse<TokenRecordListResponse>> {
    const url = buildApiUrl(API_CONFIG.ENDPOINTS.TOKEN.USER_TOKEN_LOGS);
    // 只发送有值的参数
    const params: any = {};
    if (accountId) params.accountId = accountId;
    if (phoneNumber) params.phoneNumber = phoneNumber;
    if (operationType) params.operationType = operationType;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    return http.get(url, params);
  }

  /**
   * 管理员为指定用户操作Token（充值、消耗等）
   * @param accountId 账号ID
   * @param data 操作数据
   */
  static async performOperationForUser(
    accountId: number,
    data: TokenOperationDto
  ): Promise<ApiResponse<TokenRecord>> {
    const url = buildApiUrl(API_CONFIG.ENDPOINTS.TOKEN.USER_OPERATION, {
      accountId: String(accountId),
    });
    return http.post(url, data);
  }
}

export default TokenService;
